import React, { Fragment } from 'react';
import { useSelector } from 'react-redux';
import styled, { withTheme } from 'styled-components';

import { FovTypography } from 'components/typography';
import { FovDivider, VerticalSpacer } from 'components/layout';
import { FovPagination } from 'components/generic';
import constants from 'constants/index';
import { getColor } from 'utils';

const TableContainer = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

const Header = styled('div')`
  width: 100%;
  min-height: 3rem;
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
  border-radius: 0.25rem;
  color: ${props => props.color};
  background-color: ${props => props.backgroundColor};
`;

const Column = styled('div')`
  width: ${props => (props.width ? props.width / 16 + 'rem' : '8rem')};
  display: flex;
`;

const Body = styled('div')`
  display: flex;
  background-color: ${props => props.backgroundColor};
  flex-direction: column;
`;

const DataContainer = styled(Header)`
  display: flex;
  min-height: 6.5rem;
  border: ${props =>
  props.$cellSelected
    ? '0.0625rem solid ' + getColor(props, 'accent')
    : 'none'};
  &:hover {
    border: 0.0625rem solid ${props => getColor(props, 'accentHover')
    };
  }
`;

const DataItem = styled('div')`
  line-break: anywhere;
  display: flex;
  justify-content: ${props => props.alignContent};
  width: ${props => (props.width ? props.width / 16 + 'rem' : '8rem')};
  color: ${props => props.fontColor};
`;

const NoDataContainer = styled(FovTypography)`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  color: ${props => props.color};
`;

const getColors = (colors, theme) => ({
  header: {
    backgroundColor:
      theme === constants.THEME.LIGHT
        ? colors[theme].lightGrey
        : colors[theme].darkGrey,
    font: colors[theme].onPrimary,
  },
  body: {
    backgroundColor: colors[theme].transparent,
    font: colors[theme].onPrimary,
  },
  noData: {
    color:
      theme === constants.THEME.DARK
        ? colors[theme].lightGrey
        : colors[theme].darkGrey,
  },
});

const getDataAlignContent = (alignContent = 'left') => {
  const sides = {
    right: 'flex-end',
    center: 'center',
    left: 'flex-start',
  };

  return sides[alignContent];
};

const getColumns = columns => {
  return columns.map((column, key) => (
    <Column width={column.width} key={`column-${key}`}>
      <FovTypography>{column.title}</FovTypography>
    </Column>
  ));
};

const generateData = (
  data,
  columns,
  bodyColors,
  onCellClick,
  lastModelElementRef,
  selectedTheme,
  selectedItem,
  selectedKey,
) => {
  return data.map((item, index) => {
    const isLastItem = index === data.length - 1;
    const selectedValue = item?.[selectedKey];
    return (
      <Fragment key={`data-container-${index}`}>
        <DataContainer
          $selectedTheme={selectedTheme}
          $cellSelected= { selectedValue === selectedItem}
          ref={isLastItem ? lastModelElementRef : null}
          onClick={() => onCellClick?.(item)}>
          {columns.map((column, key) => (
            <DataItem
              alignContent={getDataAlignContent(column.alignContent)}
              width={column.width}
              key={`data-item-${key}`}
              fontColor={bodyColors.font}
            >
              {column.render ? (
                column.render(item[column.key], item)
              ) : (
                <FovTypography>{item[column.key]}</FovTypography>
              )}
            </DataItem>
          ))}
        </DataContainer>
        <FovDivider />
      </Fragment>
    );
  });
};

const Table = withTheme(props => {
  const {
    theme,
    dataSource,
    columns,
    pageSize = 5,
    isPagination = false,
    onPageChange,
    currentPage,
    totalCount,
    onCellClick,
    lastModelElementRef,
    selectedGuid,
    selectedKey = 'rpc_guid',
  } = props;
  const userDataStore = useSelector(state => state.userData);
  const selectedTheme = userDataStore.userPreferences.theme;
  const { header, body, noData } = getColors(theme.colors, selectedTheme);

  return (
    <TableContainer>
      <VerticalSpacer size={15} />

      <Header backgroundColor={header.backgroundColor} color={header.font}>
        {getColumns(columns)}
      </Header>

      {totalCount ? (
        <Body backgroundColor={body.backgroundColor}>
          {generateData(
            dataSource,
            columns,
            body,
            onCellClick,
            lastModelElementRef,
            selectedTheme,
            selectedGuid,
            selectedKey,
          )}
        </Body>
      ) : (
        <NoDataContainer color={noData.color}>No Data...</NoDataContainer>
      )}

      {isPagination && (
        <FovPagination
          currentPage={currentPage}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={onPageChange}
        />
      )}
    </TableContainer>
  );
});

export { Table };